export interface SurestLocationOption {
  title: string;
  value: string;
  disabled?: boolean;
  isDisabled?: boolean;
  selected?: boolean;
}

export interface SurestLocationPayload {
  PandoraProviderKey?: string;
  PandoraProviderKeys?: string[];
}

interface LocationRecord {
  DisplayName?: unknown;
  PandoraProviderKey?: unknown;
  PandoraProviderKeys?: unknown;
  Locations?: unknown;
  title?: unknown;
  value?: unknown;
}

const ALL_LOCATIONS_VALUES = new Set(['all locations', 'all_locations']);

const asNonEmptyString = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : '';

const isAllLocations = (option: SurestLocationOption): boolean =>
  ALL_LOCATIONS_VALUES.has(asNonEmptyString(option.title).toLowerCase()) ||
  ALL_LOCATIONS_VALUES.has(asNonEmptyString(option.value).toLowerCase());

const isDisabled = (option: SurestLocationOption): boolean =>
  option.disabled === true || option.isDisabled === true;

const toLocationOption = (key: unknown, displayName?: unknown): SurestLocationOption | null => {
  const normalizedKey = asNonEmptyString(key);
  if (!normalizedKey || ALL_LOCATIONS_VALUES.has(normalizedKey.toLowerCase())) {
    return null;
  }

  const normalizedDisplayName = asNonEmptyString(displayName) || normalizedKey;
  return { title: normalizedDisplayName, value: normalizedKey };
};

export const normalizeLocationSelection = (
  selection?: SurestLocationOption | SurestLocationOption[] | null
): SurestLocationOption[] => {
  if (!selection) {
    return [];
  }

  const values = Array.isArray(selection) ? selection : [selection];
  const selectedValues = values.some(option => option?.selected === true)
    ? values.filter(option => option?.selected === true)
    : values;
  const seenKeys = new Set<string>();

  return selectedValues.filter(option => {
    if (!option || isDisabled(option) || isAllLocations(option)) {
      return false;
    }

    const key = asNonEmptyString(option.value);
    const label = asNonEmptyString(option.title);
    if (!key || !label || seenKeys.has(key)) {
      return false;
    }

    seenKeys.add(key);
    return true;
  });
};

export const buildLocationPayload = (
  selection?: SurestLocationOption | SurestLocationOption[] | null
): SurestLocationPayload => {
  const normalizedSelection = normalizeLocationSelection(selection);
  const keys = normalizedSelection.map(option => asNonEmptyString(option.value));

  if (keys.length === 1) {
    return { PandoraProviderKey: keys[0] };
  }

  if (keys.length > 1) {
    return { PandoraProviderKeys: keys };
  }

  return {};
};

const getRecordLocations = (record: LocationRecord): LocationRecord[] => {
  if (Array.isArray(record.Locations)) {
    return record.Locations.filter(Boolean) as LocationRecord[];
  }

  return [record];
};

export const extractLocationOptions = (records?: LocationRecord[] | null): SurestLocationOption[] => {
  if (!Array.isArray(records)) {
    return [];
  }

  const options: SurestLocationOption[] = [];
  const seenKeys = new Set<string>();

  records.forEach(record => {
    if (!record || typeof record !== 'object') {
      return;
    }

    getRecordLocations(record).forEach(location => {
      if (location.title && location.value && !location.DisplayName && !location.PandoraProviderKey && !location.PandoraProviderKeys) {
        const option = { title: asNonEmptyString(location.title), value: asNonEmptyString(location.value) };
        if (option.title && option.value && !seenKeys.has(option.value)) {
          seenKeys.add(option.value);
          options.push(option);
        }
        return;
      }

      const responseKeys = Array.isArray(location.PandoraProviderKeys)
        ? location.PandoraProviderKeys
        : [location.PandoraProviderKey || location.title];

      responseKeys.forEach(key => {
        const option = toLocationOption(key, location.DisplayName || location.value);
        if (option && !seenKeys.has(option.value)) {
          seenKeys.add(option.value);
          options.push(option);
        }
      });
    });
  });

  return options;
};
